/**
 * React Components Index
 *
 * This file imports and registers all React components with the registry.
 * Add new components here to make them available for mounting from Rails.
 *
 * Component Registration Pattern:
 * 1. Import the component
 * 2. Register with a unique name that matches Rails usage
 *
 * Example:
 *   import MyComponent from './MyComponent';
 *   registry.register('MyComponent', MyComponent);
 */

// React
import { lazy } from 'react';

// Local components
import registry from '../registry';
import ActivityCentre from './ActivityCentre/index';
import Sidebar from './Sidebar/index';
import { MobileSidebarDrawer } from './MobileSidebar/index';
import UserManagement from './UserManagement/index';
import OrderDetails from './OrderDetails/index';
import OrderManagement from './OrderManagement/index';
import InventoryManagement from './InventoryManagement/index';
import AppRouter from '../router/AppRouter';
import EventManagement from './EventManagement/index';
import ProductManagement from './ProductManagement/index';
import PriceModifiers from './PriceModifiers/index';
import ResaleItemManagement from './ResaleItemManagement/index';
import OrderSourceManagement from './OrderSourceManagement/index';

// Lazy-loaded components (only fetched when their page is visited)
const EditEvent = lazy(() => import('./EditEvent/index'));
const EditEventHeader = lazy(() => import('./EditEvent/EditEventHeader'));
const InventoryEdit = lazy(() => import('./Inventory/EditInventory/index'));
const InventoryEditHeader = lazy(() => import('./Inventory/EditInventory/EditInventoryHeader'));
const InventoryDetails = lazy(() => import('./InventoryDetails/index'));
const InventoryDetailsHeader = lazy(() => import('./InventoryDetails/InventoryDetailsHeader'));
const ProductDetails = lazy(() => import('./ProductDetails/index'));
const ProductDetailsHeader = lazy(() => import('./ProductDetails/ProductDetailsHeader'));
const PriceModifierDetails = lazy(() => import('./PriceModifierDetails/index'));
const PriceModifierDetailsHeader = lazy(() => import('./PriceModifierDetails/PriceModifierDetailsHeader'));
const ResaleItemDetails = lazy(() => import('./ResaleItemDetails/index'));
const ResaleItemDetailsHeader = lazy(() => import('./ResaleItemDetails/ResaleItemDetailsHeader'));
const OrderSourceDetails = lazy(() => import('./OrderSourceDetails/index'));
const EditOrderSource = lazy(() => import('./EditOrderSource/index'));
const EditProduct = lazy(() => import('./EditProduct/index'));
const EditProductHeader = lazy(() => import('./EditProduct/EditProductHeader'));
const EditResaleItem = lazy(() => import('./EditResaleItem/index'));
const EditResaleItemHeader = lazy(() => import('./EditResaleItem/EditResaleItemHeader'));
const EditPriceModifier = lazy(() => import('./EditPriceModifier/index'));
const EditPriceModifierHeader = lazy(() => import('./EditPriceModifier/EditPriceModifierHeader'));
const EditOrderDetails = lazy(() => import('./EditOrderDetails/index'));
const EditOrderDetailsHeader = lazy(() => import('./EditOrderDetails/EditOrderDetailsHeader'));
const AccountSettings = lazy(() => import('./AccountSettings/index'));

registry.register('ActivityCentre', ActivityCentre);
registry.register('Sidebar', Sidebar);
registry.register('MobileSidebarDrawer', MobileSidebarDrawer);
registry.register('UserManagement', UserManagement);
registry.register('OrderDetails', OrderDetails);
registry.register('OrderManagement', OrderManagement);
registry.register('InventoryManagement', InventoryManagement);
registry.register('AppRouter', AppRouter);
registry.register('EventManagement', EventManagement);
registry.register('ProductManagement', ProductManagement);
registry.register('PriceModifiers', PriceModifiers);
registry.register('ResaleItemManagement', ResaleItemManagement);
registry.register('OrderSourceManagement', OrderSourceManagement);
registry.register('EditEvent', EditEvent);
registry.register('EditEventHeader', EditEventHeader);
registry.register('InventoryEdit', InventoryEdit);
registry.register('InventoryEditHeader', InventoryEditHeader);
registry.register('InventoryDetails', InventoryDetails);
registry.register('InventoryDetailsHeader', InventoryDetailsHeader);
registry.register('ProductDetails', ProductDetails);
registry.register('ProductDetailsHeader', ProductDetailsHeader);
registry.register('PriceModifierDetails', PriceModifierDetails);
registry.register('PriceModifierDetailsHeader', PriceModifierDetailsHeader);
registry.register('ResaleItemDetails', ResaleItemDetails);
registry.register('ResaleItemDetailsHeader', ResaleItemDetailsHeader);
registry.register('OrderSourceDetails', OrderSourceDetails);
registry.register('EditOrderSource', EditOrderSource);
registry.register('EditProduct', EditProduct);
registry.register('EditProductHeader', EditProductHeader);
registry.register('EditResaleItem', EditResaleItem);
registry.register('EditResaleItemHeader', EditResaleItemHeader);
registry.register('EditPriceModifier', EditPriceModifier);
registry.register('EditPriceModifierHeader', EditPriceModifierHeader);
registry.register('EditOrderDetails', EditOrderDetails);
registry.register('EditOrderDetailsHeader', EditOrderDetailsHeader);
registry.register('AccountSettings', AccountSettings);

// Export components for direct usage if needed
export { ActivityCentre, AccountSettings, UserManagement, OrderDetails, OrderManagement, InventoryManagement, EventManagement, ProductManagement, PriceModifiers, PriceModifierDetails, PriceModifierDetailsHeader, ResaleItemManagement, OrderSourceManagement, ProductDetails, ProductDetailsHeader, ResaleItemDetails, ResaleItemDetailsHeader, OrderSourceDetails, EditOrderSource, EditProduct, EditProductHeader, EditResaleItem, EditResaleItemHeader, EditPriceModifier, EditPriceModifierHeader, EditEvent, EditEventHeader, InventoryEdit, InventoryEditHeader, InventoryDetails, InventoryDetailsHeader, EditOrderDetails, EditOrderDetailsHeader };
